import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { api } from "../../lib/api";
import { logError } from "../../lib/log";
import {
  TrendingUp, TrendingDown, AlertTriangle, Loader2, ArrowRight,
  Wallet, Building, MapPin, Activity, Zap, Lock,
} from "lucide-react";

const fmtL = (v) => v == null || isNaN(v) ? "—" : "₹" + Number(v).toFixed(1) + "L";
const fmtINR = (v) => v == null || isNaN(v) ? "—" : "₹" + Math.round(v).toLocaleString("en-IN");
const fmtPct = (v) => v == null || isNaN(v) ? "—" : `${Number(v).toFixed(1)}%`;
const fmtNum = (v) => v == null || isNaN(v) ? "—" : Math.round(v).toLocaleString("en-IN");
const opexTone = (pct) =>
  pct > 90 ? "text-rose-700 bg-rose-50 border-rose-200" :
  pct > 75 ? "text-amber-700 bg-amber-50 border-amber-200" :
  "text-emerald-700 bg-emerald-50 border-emerald-200";

export default function MisDashboard() {
  const [data, setData] = useState(null);
  const [outliers, setOutliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, o] = await Promise.all([
          api.get("/mis-analysis/network-summary"),
          api.get("/mis-analysis/outliers"),
        ]);
        setData(s.data);
        setOutliers(o.data.outliers || []);
      } catch (e) { logError("mis dashboard fetch failed", e); }
      finally { setLoading(false); }
    })();
  }, []);

  const totalCategoryValue = useMemo(
    () => (data?.top_categories || []).reduce((a, c) => a + c.amount, 0) || 1,
    [data],
  );

  return (
    <AppShell title="MIS Analysis · Company Level" subtitle={data?.month ? `Latest snapshot · ${data.month}` : "Cost intelligence"} persona="mis">
      {loading ? (
        <div className="flex items-center justify-center h-96 text-zinc-500" data-testid="mis-dash-loading">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading Company Level summary…
        </div>
      ) : (
        <div className="space-y-6">
          {/* Top KPI row: 8 KPIs */}
          <section className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3" data-testid="mis-dash-kpis">
            <KPI label="Properties" value={data?.operational_properties} sub={`${data?.state_rollup?.length || 0} states · ${fmtNum(data?.total_capacity_beds_per_day)} rated capacity`} accent="#FFCC00" />
            <KPI label="Avg occupancy" value={fmtPct(data?.network_avg_occ_pct)} sub={`${fmtNum(data?.total_sold_beds_per_day)} sold ÷ ${fmtNum(data?.total_available_beds_per_day)} available beds/day`} accent="#FF69B4" icon={Activity} />
            <KPI label="ADR / bed" value={fmtINR(data?.network_adr)} sub={`Total rev ÷ (sold × ${data?.days_in_month || 30} days)`} accent="#9370DB" />
            <KPI label="Revenue" value={fmtL(data?.total_revenue_lakhs)} accent="#20B2AA" />
            <KPI label="Expense" value={fmtL(data?.total_expense_lakhs)} sub={`${fmtPct(data?.network_opex_ratio_pct)} of rev`} accent="#FFA500" />
            <KPI label="EBITDA" value={fmtL(data?.ebitda_lakhs)} tone={data?.ebitda_lakhs < 0 ? "rose" : "emerald"} accent="#9370DB" />
            <KPI label="Variable cost" value={fmtL(data?.variable_cost_lakhs)} sub={`${fmtPct(data?.variable_share_pct)} of opex`} accent="#FF69B4" icon={Zap} />
            <KPI label="Cost / bed-night" value={fmtINR(data?.network_cost_per_bed_night)} sub="Company avg" accent="#20B2AA" />
          </section>

          {/* Top categories + Top cost cities */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <section className="lg:col-span-5 bg-white border border-zinc-200 rounded-xl p-5" data-testid="mis-dash-categories">
              <header className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Where the money goes</div>
                  <h2 className="font-display font-black text-lg tracking-tight">Top expense heads</h2>
                </div>
                <div className="text-[10.5px] flex items-center gap-3">
                  <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-400" /><span className="text-zinc-600">VAR (occupancy-linked)</span></span>
                  <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-zinc-400" /><span className="text-zinc-600">FIX</span></span>
                </div>
              </header>
              <ul className="space-y-2">
                {(data?.top_categories || []).slice(0, 10).map((c) => {
                  const share = (c.amount / totalCategoryValue) * 100;
                  const barColor = c.is_variable ? "#FB7185" : "#9CA3AF";
                  return (
                    <li key={c.category} className="space-y-1" data-testid={`mis-dash-cat-${c.category.replace(/\W/g, '')}`}>
                      <div className="flex justify-between text-[12px]">
                        <span className="font-medium text-zinc-800">{c.category}<span className={`ml-2 text-[9px] uppercase font-bold tracking-wider ${c.is_variable ? "text-rose-700" : "text-zinc-500"}`}>{c.is_variable ? "VAR" : "FIX"}</span></span>
                        <span className="tabular-nums text-zinc-900 font-bold">{fmtINR(c.amount)}<span className="text-zinc-400 font-normal ml-2">{share.toFixed(1)}%</span></span>
                      </div>
                      <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${share}%`, background: barColor }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="lg:col-span-7 bg-white border border-zinc-200 rounded-xl p-5" data-testid="mis-dash-cities">
              <header className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Property burn</div>
                  <h2 className="font-display font-black text-lg tracking-tight">Top cost cities — {data?.month}</h2>
                </div>
                <Building className="w-4 h-4 text-teal-600" />
              </header>
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                    <th className="py-2">Property</th>
                    <th className="py-2 text-right">Occ %</th>
                    <th className="py-2 text-right">Revenue</th>
                    <th className="py-2 text-right">Expense</th>
                    <th className="py-2 text-right">EBITDA</th>
                    <th className="py-2 text-right">₹/bed-night</th>
                    <th className="py-2 text-right">Opex %</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.top_cost_cities || []).map((c) => (
                    <tr key={c.location} className="border-b border-zinc-100 hover:bg-zinc-50/70 cursor-pointer" onClick={() => navigate(`/mis/property-tracker?location=${encodeURIComponent(c.location)}`)} data-testid={`mis-dash-city-${c.location}`}>
                      <td className="py-2">
                        <div className="font-bold text-zinc-900">{c.location}</div>
                        <div className="text-[10px] text-zinc-500">{c.type} · {c.model}</div>
                      </td>
                      <td className="py-2 text-right tabular-nums">{fmtPct(c.occ_total_pct)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtINR(c.total_revenue)}</td>
                      <td className="py-2 text-right tabular-nums text-rose-700">{fmtINR(c.total_expense)}</td>
                      <td className={`py-2 text-right tabular-nums ${c.ebitda < 0 ? "text-rose-700 font-bold" : "text-emerald-700"}`}>{fmtINR(c.ebitda)}</td>
                      <td className="py-2 text-right tabular-nums">{fmtINR(c.cost_per_bed_night)}</td>
                      <td className="py-2 text-right">
                        <span className={`inline-block px-2 py-0.5 rounded border tabular-nums ${opexTone(c.opex_ratio_pct)}`}>{c.opex_ratio_pct}%</span>
                      </td>
                      <td className="py-2 text-right text-zinc-400"><ArrowRight className="w-3.5 h-3.5 inline" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          </div>

          {/* Variable vs Fixed strip + Outlier list */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <section className="lg:col-span-5 bg-white border border-zinc-200 rounded-xl p-5" data-testid="mis-dash-varfix">
              <header className="mb-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Cost structure</div>
                <h2 className="font-display font-black text-lg tracking-tight">Variable vs Fixed cost</h2>
              </header>
              <div className="flex items-stretch gap-4">
                <div className="flex-1 bg-rose-50 border border-rose-200 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-rose-700">
                    <Zap className="w-3.5 h-3.5" /> Variable
                  </div>
                  <div className="font-display font-black text-rose-900 tabular-nums mt-1" style={{ fontSize: 26, lineHeight: 1.3 }}>{fmtL(data?.variable_cost_lakhs)}</div>
                  <div className="text-[11px] text-rose-700 mt-0.5">{fmtPct(data?.variable_share_pct)} of total opex</div>
                  <div className="text-[10.5px] text-rose-600/80 mt-2">Scales with occupancy — F&B, HK, Laundry, Utilities, OTA …</div>
                </div>
                <div className="flex-1 bg-zinc-50 border border-zinc-200 rounded-lg p-3">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-zinc-700">
                    <Lock className="w-3.5 h-3.5" /> Fixed
                  </div>
                  <div className="font-display font-black text-zinc-900 tabular-nums mt-1" style={{ fontSize: 26, lineHeight: 1.3 }}>{fmtL(data?.fixed_cost_lakhs)}</div>
                  <div className="text-[11px] text-zinc-600 mt-0.5">{fmtPct(100 - (data?.variable_share_pct || 0))} of total opex</div>
                  <div className="text-[10.5px] text-zinc-500 mt-2">Stays put — Rent, Salary, Finance Cost, Depreciation …</div>
                </div>
              </div>
              <div className="mt-4 h-3 rounded-full overflow-hidden flex">
                <div className="bg-rose-400" style={{ width: `${data?.variable_share_pct || 0}%` }} />
                <div className="bg-zinc-300 flex-1" />
              </div>
            </section>

            <section className="lg:col-span-7 bg-white border border-zinc-200 rounded-xl p-5" data-testid="mis-dash-outliers">
              <header className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600">Watchlist</div>
                  <h2 className="font-display font-black text-lg tracking-tight">Outlier auto-flags</h2>
                </div>
                <div className="flex items-center gap-2 text-[10.5px]">
                  <AlertTriangle className="w-4 h-4 text-rose-600" />
                  <span className="font-bold text-rose-700">{outliers.length}</span>
                  <button onClick={() => navigate("/mis/outliers")} className="text-teal-700 hover:underline font-bold uppercase tracking-wider">See all</button>
                </div>
              </header>
              <ul className="space-y-2">
                {outliers.slice(0, 6).map((o) => (
                  <li key={o.location} className="border border-rose-200 bg-rose-50/40 rounded-md p-2.5 flex items-start justify-between gap-3 hover:bg-rose-50 cursor-pointer" onClick={() => navigate(`/mis/property-tracker?location=${encodeURIComponent(o.location)}`)} data-testid={`mis-dash-outlier-${o.location}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <div className="font-bold text-zinc-900 text-[13px]">{o.location}</div>
                        {o.occ_total_pct ? <span className="text-[10px] text-rose-700 bg-white border border-rose-200 rounded px-1.5 py-0.5">Occ {fmtPct(o.occ_total_pct)}</span> : null}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {o.flags.map((f) => (
                          <span key={f} className="text-[10px] bg-white border border-rose-200 text-rose-800 rounded px-1.5 py-0.5">{f}</span>
                        ))}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[10px] uppercase tracking-wider text-rose-600 font-bold">EBITDA</div>
                      <div className={`font-display font-black tabular-nums ${o.ebitda_lakhs < 0 ? "text-rose-800" : "text-emerald-800"}`} style={{ fontSize: 16, lineHeight: 1.4 }}>
                        {o.ebitda_lakhs < 0 ? <TrendingDown className="inline w-3.5 h-3.5 mr-0.5" /> : <TrendingUp className="inline w-3.5 h-3.5 mr-0.5" />}
                        {fmtL(o.ebitda_lakhs)}
                      </div>
                    </div>
                  </li>
                ))}
                {outliers.length === 0 && (
                  <li className="text-center text-[12px] text-emerald-700 py-4 italic">All clear — no expense outliers detected.</li>
                )}
              </ul>
            </section>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function KPI({ label, value, sub, accent, tone, icon: Icon }) {
  const toneCls = tone === "rose" ? "text-rose-800" : tone === "emerald" ? "text-emerald-800" : "text-zinc-900";
  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-3.5" data-testid={`kpi-${label.replace(/\W/g, '-').toLowerCase()}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-zinc-500">
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {label}
      </div>
      <div className={`font-display font-black tabular-nums mt-1.5 ${toneCls}`} style={{ fontSize: 20, lineHeight: 1.3, paddingBottom: 2 }}>{value}</div>
      {sub && <div className="text-[10.5px] text-zinc-500 mt-0.5">{sub}</div>}
      <div className="mt-2 h-[3px] w-7 rounded-full" style={{ background: accent }} />
    </div>
  );
}
